import type { ReactNode } from "react";
import { AppLink, StatusPopover, cx, formatCount } from "@amber/ui";

export function Panel({
  title,
  lead,
  link,
  className,
  children,
  labelledBy,
}: {
  title: string;
  lead?: ReactNode;
  link?: { href: string; label: string } | null;
  className?: string;
  children: ReactNode;
  labelledBy: string;
}) {
  return (
    <section aria-labelledby={labelledBy} className={cx("min-w-0 rounded-xl border border-divider bg-bg", className)}>
      <header className="flex items-baseline gap-2.5 px-4 pt-3 pb-2">
        <h2 id={labelledBy} className="m-0 text-base font-semibold">
          {title}
        </h2>
        {lead ? <span className="min-w-0 truncate text-md text-muted">{lead}</span> : null}
        {link ? (
          <AppLink
            href={link.href}
            className="ml-auto flex-none text-md font-strong text-link no-underline hover:underline"
          >
            {link.label} →
          </AppLink>
        ) : null}
      </header>
      <div className="px-4 pb-3.5">{children}</div>
    </section>
  );
}

export function Count({
  href,
  count,
  team,
  label,
  inline = false,
}: {
  href: string;
  count: number;
  team: boolean;
  label?: string;
  inline?: boolean;
}) {
  if (team) return <span>{formatCount(count)}</span>;
  return (
    <AppLink
      href={href}
      aria-label={label ? `${label}: ${formatCount(count)}` : undefined}
      className={cx("text-link", inline ? "underline decoration-1 underline-offset-2" : "no-underline hover:underline")}
    >
      {formatCount(count)}
    </AppLink>
  );
}

export function Pop({ label, team, children }: { label: ReactNode; team: boolean; children: ReactNode }) {
  if (team) return <span>{label}</span>;
  return <StatusPopover label={label}>{children}</StatusPopover>;
}

export function Tile({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0 border-r border-divider px-4 first:pl-0 last:border-r-0 max-narrow:border-r-0 max-narrow:px-0 max-narrow:pb-3">
      <div className="mb-1 text-md">{label}</div>
      {children}
    </div>
  );
}
